import { useEffect, useState } from "react";

import {
  makeMetabolicFetchPage,
  METABOLIC_READ_MAX_PAGES,
  paginate,
  type GlucoseReading,
  type NutritionEntry,
} from "@heraion/shared";

import { apiFetch } from "./api";

// The web console's metabolic resource — the glucose + nutrition rows the
// Health Connect merge imported, for the shared MetabolicCard. Online only
// (no offline outbox; that stays on mobile), read-only. Same endpoints as
// the mobile useMetabolic, and the same windowing AND cursor-walk, no
// longer by convention: both build the page request from `@heraion/shared`
// (metabolicRead.ts), so the two cards cannot drift to different spans, and
// neither silently truncates past page one (HC-22). Newest-first from the
// API; the card reverses for the chart.

export type UseMetabolicWeb = {
  glucose: GlucoseReading[];
  nutrition: NutritionEntry[];
  loading: boolean;
};

export function useMetabolicWeb(): UseMetabolicWeb {
  const [glucose, setGlucose] = useState<GlucoseReading[]>([]);
  const [nutrition, setNutrition] = useState<NutritionEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      // One `nowMs` for both reads AND every page of each, so `since`
      // can't drift mid-walk (shared with mobile via metabolicRead.ts).
      const nowMs = Date.now();
      try {
        const [g, n] = await Promise.all([
          paginate(
            makeMetabolicFetchPage<GlucoseReading>(
              "/api/me/glucose",
              apiFetch,
              nowMs,
            ),
            { maxPages: METABOLIC_READ_MAX_PAGES },
          ),
          paginate(
            makeMetabolicFetchPage<NutritionEntry>(
              "/api/me/nutrition",
              apiFetch,
              nowMs,
            ),
            { maxPages: METABOLIC_READ_MAX_PAGES },
          ),
        ]);
        if (cancelled) return;
        setGlucose(g.items);
        setNutrition(n.items);
      } catch {
        // Offline / no data yet — leave empty, the card shows its hint.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { glucose, nutrition, loading };
}
